import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import API from '../../config/api';
import './QureHeader.css';

const QureHeader = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [profileDropdownOpen, setProfileDropdownOpen] = useState(false);
  const [qrModalOpen, setQrModalOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const profileDropdownRef = useRef(null);
  const qrCodeRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (profileDropdownRef.current && !profileDropdownRef.current.contains(event.target)) {
        setProfileDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Generate QR Code
  useEffect(() => {
    const generateQRCode = async () => {
      if (qrModalOpen && user && qrCodeRef.current) {
        try {
          // Fetch encrypted QR code string from backend
          const response = await API.get('/qure/profile/qr-code');
          if (response.data.success && response.data.encrypted_string) {
            // Clear previous content
            qrCodeRef.current.innerHTML = '';
            
            // Create QR code using external API with encrypted string
            const img = document.createElement('img');
            img.src = `https://api.qrserver.com/v1/create-qr-code/?size=256x256&data=${encodeURIComponent(response.data.encrypted_string)}`;
            img.alt = 'QR Code';
            img.style.width = '100%';
            img.style.maxWidth = '256px';
            img.style.height = 'auto';
            qrCodeRef.current.appendChild(img);
          }
        } catch (error) {
          console.error('Error generating QR code:', error);
          qrCodeRef.current.innerHTML = '<p style="color: red;">Error loading QR code</p>';
        }
      }
    };
    
    generateQRCode();
  }, [qrModalOpen, user]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const isActive = (path) => {
    return location.pathname === path;
  };

  return (
    <>
      <header className="qure-header">
        <div className="qure-header-left">
          <button 
            className="qure-sidebar-toggle" 
            type="button" 
            onClick={() => setSidebarOpen(!sidebarOpen)}
            aria-label="Toggle Sidebar"
          >
            <i className="bi bi-list"></i>
          </button>
          <Link to="/qure-dashboard" className="header-brand me-auto">
            <img src="https://qure.science/images/qure.webp" alt="Qure" className="header-logo" />
          </Link>
          <span className="qure-user-type-badge">Qure</span>
        </div>
        
        <div className="qure-header-right">
          <button 
            className="qure-qr-button" 
            onClick={() => setQrModalOpen(true)}
            type="button"
          >
            <i className="bi bi-qr-code"></i>
            <span>QR Code</span>
          </button>

          <div className="qure-profile-dropdown" ref={profileDropdownRef}>
            <button 
              className="qure-profile-menu" 
              type="button"
              onClick={() => setProfileDropdownOpen(!profileDropdownOpen)}
            >
              <img 
                src={`https://ui-avatars.com/api/?name=${encodeURIComponent(user?.username || 'User')}&background=17a2b8&color=fff`}
                alt="Profile" 
                className="qure-profile-img"
              />
              <span className="qure-profile-name">{user?.username || 'User'}</span>
              <i className={`bi bi-chevron-down qure-profile-chevron ${profileDropdownOpen ? 'rotated' : ''}`}></i>
            </button>
            {profileDropdownOpen && (
              <div className="qure-dropdown-menu show">
                <Link className="qure-dropdown-item" to="/profile" onClick={() => setProfileDropdownOpen(false)}>
                  <i className="bi bi-person"></i>Profile
                </Link>
                {(user?.user_id === 79 || user?.user_id === '79' || parseInt(user?.user_id) === 79) && (
                  <Link className="qure-dropdown-item" to="/admin-dashboard" onClick={() => setProfileDropdownOpen(false)}>
                    <i className="bi bi-shield-check"></i>Admin Dashboard
                  </Link>
                )}
                <hr className="qure-dropdown-divider" />
                <button className="qure-dropdown-item" onClick={handleLogout}>
                  <i className="bi bi-box-arrow-right"></i>Sign Out
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* QR Code Modal */}
      {qrModalOpen && (
        <div className="qure-qr-modal show" onClick={() => setQrModalOpen(false)}>
          <div className="qure-qr-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="qure-qr-modal-header">
              <h3 className="qure-qr-modal-title">
                <i className="bi bi-qr-code"></i> Your QR Code
              </h3>
              <button 
                className="qure-qr-close" 
                onClick={() => setQrModalOpen(false)}
                type="button"
              >
                <i className="bi bi-x-lg"></i>
              </button>
            </div>
            <div className="qure-qr-modal-body">
              <div className="qure-qr-code-container">
                <div id="qureQRCode" ref={qrCodeRef}></div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Sidebar */}
      <aside className={`qure-sidebar ${sidebarOpen ? 'qure-show' : ''}`} id="qureSidebar">
        <nav>
          <Link 
            to="/qure-dashboard" 
            className={`qure-nav-link ${isActive('/qure-dashboard') ? 'qure-active' : ''}`}
            onClick={() => setSidebarOpen(false)}
          >
            <i className="bi bi-house"></i>Dashboard
          </Link>
          <Link 
            to="/my-teams" 
            className={`qure-nav-link ${isActive('/my-teams') ? 'qure-active' : ''}`}
            onClick={() => setSidebarOpen(false)}
          >
            <i className="bi bi-people"></i>My Teams
          </Link>

          <div className="qure-nav-section">Experiments</div>
          <Link 
            to="/qure-calendar" 
            className={`qure-nav-link ${isActive('/qure-calendar') ? 'qure-active' : ''}`}
            onClick={() => setSidebarOpen(false)}
          >
            <i className="bi bi-calendar-check"></i>Calendar
          </Link>
          <Link 
            to="/qure-analytics" 
            className={`qure-nav-link ${isActive('/qure-analytics') ? 'qure-active' : ''}`}
            onClick={() => setSidebarOpen(false)}
          >
            <i className="bi bi-graph-up"></i>Experiment Analytics
          </Link>

          <div className="qure-nav-section">Support</div>
          <Link 
            to="/help-desk" 
            className={`qure-nav-link ${isActive('/help-desk') ? 'qure-active' : ''}`}
            onClick={() => setSidebarOpen(false)}
          >
            <i className="bi bi-headset"></i>Help Desk
          </Link>
          <Link 
            to="/support" 
            className={`qure-nav-link ${isActive('/support') ? 'qure-active' : ''}`}
            onClick={() => setSidebarOpen(false)}
          >
            <i className="bi bi-headset"></i>Support Ticket
          </Link>
        </nav>
      </aside>

    </>
  );
};

export default QureHeader;
